import { useQuery } from "@apollo/client";
import type { IOptions } from "@fluidattacks/design";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { usePackagesStore } from "./use-packages-store";

import { GET_TOE_PACKAGE_IMAGES } from "../../queries";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

interface IGetPackagesImagesResponse {
  images: IOptions[];
  loading: boolean;
}

const useGetPackagesImages = (
  groupName: string,
): IGetPackagesImagesResponse => {
  const { t } = useTranslation();
  const defaultImages = usePackagesStore((store): string[] => store.images);

  const { data: packageImages, loading } = useQuery(GET_TOE_PACKAGE_IMAGES, {
    fetchPolicy: "no-cache",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.warning("An error occurred loading Toe package stats", error);
      });
    },
    variables: { groupName },
  });

  const images = useMemo((): IOptions[] => {
    const edges = packageImages?.group.toePackageStats.dockerImages ?? [];
    const exists = edges.some((image): boolean =>
      defaultImages.includes(image),
    );

    const list = edges.map((image): IOptions => {
      return {
        checked: defaultImages.includes(image),
        label: image,
        value: image,
      };
    });

    return exists || defaultImages.length === 0
      ? list
      : [
          ...list,
          { checked: true, label: defaultImages[0], value: defaultImages[0] },
        ];
  }, [defaultImages, packageImages?.group.toePackageStats.dockerImages]);

  return { images, loading };
};

export { useGetPackagesImages };
